import React, { FC, useEffect } from 'react';
import PaginationWithYearAndMonth, {
  IPaginationWithYearAndMonth,
} from 'components/Paginations/PaginationWithYearAndMonth';
import PaginationContainer from 'components/Paginations/PaginationContainer';
import { DEFAULT_PAGINATION_VALUE, PaginationLabels, Value as PaginationValue } from 'components/Paginations';
import { DEFAULT_DATE_RANGE_PICKER_VALUE, DateRange } from 'components/DateRangePicker';
import { mergeTwoObjects } from 'utils';
import BasicTable, { BasicTableProps, FilterOptions, RequestFilters, SelectedFind, SortValue } from './BasicTable';
import TableWrapper, { TableWrapperProps } from './TableWrapper';

export type TableWithYearAndMonthPaginationChanges = RequestFilters & {
  pagination?: PaginationValue;
  dates?: DateRange;
  accountType?: string;
  show?: string;
  month?: number;
  year?: number;
};

export type TableWithYearAndMonthPaginationProps = Pick<
  BasicTableProps,
  'cellRenderer' | 'data' | 'columns' | 'tableLayout' | 'stickyHeader' | 'stickyHeaderOffsets' | 'hasNoFilters'
> & {
  tableWrapperProps?: TableWrapperProps;
  paginationLabels?: PaginationLabels;
  filtersData?: {
    find?: FilterOptions;
    count: number;
  };
  filters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
    year?: string;
    month?: string;
    show?: string;
  };
  defaultFilters?: {
    order?: SortValue;
    find?: SelectedFind;
    dates?: DateRange;
    pagination?: PaginationValue;
    accountType?: string;
    show?: string;
  };
  shouldApplyChangesOnMount?: boolean;
  shouldUseDefaultDatesOnMount?: boolean;
  isNotEndDateLimited?: boolean;
  applyChanges: (value: TableWithYearAndMonthPaginationChanges) => void;
  hasFixedPagination?: boolean;
  dateFormat?: string;
  optionsForSelect: any;
  labelsForSelect: any;
};

const TableWithYearAndMonthPagination: FC<TableWithYearAndMonthPaginationProps> = ({
  data,
  columns,
  cellRenderer,
  tableWrapperProps,
  paginationLabels,
  filtersData,
  filters,
  defaultFilters,
  shouldApplyChangesOnMount,
  shouldUseDefaultDatesOnMount = true,
  applyChanges,
  isNotEndDateLimited,
  hasFixedPagination = true,
  dateFormat = 'DMY',
  tableLayout,
  stickyHeader,
  stickyHeaderOffsets,
  hasNoFilters,
  optionsForSelect,
  labelsForSelect,
}) => {
  const dateRangePickerProps = mergeTwoObjects(
    {
      value: filters?.dates,
      defaultValue: defaultFilters?.dates || DEFAULT_DATE_RANGE_PICKER_VALUE,
      isNotEndDateLimited,
      onChange: (value: DateRange) => applyChanges({ dates: value }),
      dateFormat,
    },
    {},
  );

  const showTypeSelectProps = mergeTwoObjects(
    {
      value: filters?.show,
      onChange: (value: string) => applyChanges({ show: value }),
    },
    {},
  );

  const monthSelectProps = mergeTwoObjects(
    {
      options: optionsForSelect.month,
      label: labelsForSelect.month,
      value: filters?.month,
      onChange: (value: number) => applyChanges({ month: value }),
    },
    {},
  );

  const yearSelectProps = mergeTwoObjects(
    {
      options: optionsForSelect.year,
      label: labelsForSelect.year,
      value: filters?.year,
      onChange: (value: number) => applyChanges({ year: value }),
    },
    {},
  );

  const paginationProps = mergeTwoObjects(
    {
      monthSelectProps,
      yearSelectProps,
      showTypeSelectProps,
      dateRangePickerProps,
      count: filtersData?.count,
      value: filters?.pagination,
      defaultValue: defaultFilters?.pagination || DEFAULT_PAGINATION_VALUE,
      paginationLabels: paginationLabels,
      onChange: (value: PaginationValue) => applyChanges({ pagination: value }),
    },
    {},
  ) as IPaginationWithYearAndMonth;

  const tableProps = mergeTwoObjects(
    {
      data,
      columns,
      cellRenderer,
      filtersData,
      filters,
      defaultFilters,
      applyChanges: (value: RequestFilters) => applyChanges(value),
      tableLayout,
      stickyHeader,
      stickyHeaderOffsets,
      hasNoFilters,
    },
    {},
  ) as BasicTableProps;

  useEffect(() => {
    const shouldApply = typeof shouldApplyChangesOnMount === 'boolean' ? shouldApplyChangesOnMount : true;
    if (shouldApply) {
      applyChanges(
        mergeTwoObjects(filters || {}, {
          ...(shouldUseDefaultDatesOnMount ? { dates: dateRangePickerProps.defaultValue } : {}),
          pagination: paginationProps.defaultValue,
          ...(defaultFilters || {}),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableWrapper {...(tableWrapperProps || {})}>
      <PaginationContainer top={0} isFixed={hasFixedPagination}>
        <PaginationWithYearAndMonth {...paginationProps} />
      </PaginationContainer>
      <BasicTable {...tableProps} stickyHeader={hasFixedPagination || stickyHeader} shouldApplyChangesOnMount={false} />
    </TableWrapper>
  );
};

export default TableWithYearAndMonthPagination;
